import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import { find } from 'lodash';
import React from 'react';

import { useLagoon } from '../../hooks/useLagoon';
import { StoreKeys } from '../../hooks/useStore/state';
import { useStore } from '../../hooks/useStore/useStore';
import {
  AcoProvider,
  ProviderDetails,
  ProviderLocation,
  RecommendationDetails,
} from '../../models/ProviderDetails';
import { capitalizeFirstLetterOfEachWord } from '../../utils/providerNames.utils';
import {
  RecommendationCriteriaMatch,
  getChipbackgroundColor,
} from '../../utils/smartChoice.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { RecommendationProp } from '../Widget/SmartChoiceWidget/PreferencesForm';
import { BenifitsBreakDown } from './BenefitsBreakDown/BenfitsBreakDown';
import { ConvenienceBreakDown } from './ConvenienceBreakDown/ConvenienceBreakDown';
import { PersonalFitBreakDown } from './PersonalFitBreakDown/PersonalFitBreakDown';
import { QualityBreakDown } from './QualityBreakDown/QualityBreakDown';
import {
  BackButtonContainer,
  StyledDrawer,
} from './SmartChoiceBreakDown.styles';
import { SmartChoiceBreakDownWidgetHeader } from './SmartChoiceBreakDownWidgetHeader';

interface BreakdownDrawerProps {
  drawerType: string;
  isOpen: boolean;
  preferencesData?: RecommendationProp;
  providerDetails: ProviderDetails;
  locationKey: string | undefined;
  recommendationDetails: RecommendationDetails;
  setIsOpen: () => void;
  haveBackButton?: boolean;
  setSmarChoiceBreakDownL1?: (isOpen: boolean) => void;
  setFromL2DrawerToHaveBackButton?: () => void;
  handleTabChange: (tab: number) => void;
  isWidget?: boolean;
  isCareAvl?: boolean;
  isTieredPlan?: boolean;
}
export const BreakdownDrawer = ({
  drawerType,
  isOpen,
  preferencesData,
  providerDetails,
  locationKey,
  recommendationDetails,
  setIsOpen,
  handleTabChange,
  haveBackButton,
  setSmarChoiceBreakDownL1,
  setFromL2DrawerToHaveBackButton,
  isWidget,
  isCareAvl,
  isTieredPlan,
}: BreakdownDrawerProps) => {
  const selectedLocation: ProviderLocation =
    providerDetails?.providerLocations?.find(
      (x) => x.locationKey === locationKey
    ) as ProviderLocation;
  const selectedLocatoionCity = `${selectedLocation?.address?.line?.[0]}, ${selectedLocation?.address?.city}, ${selectedLocation?.address?.state}`;
  const setShowSmartChoiceLevel1Breakdown = useStore(
    StoreKeys.SET_SHOW_SMART_CHOICE_LEVEL1_BREAKDOWN
  );
  const setShowSmartChoiceLevel3Breakdown = useStore(
    StoreKeys.SET_SHOW_SMART_CHOICE_LEVEL3_BREAKDOWN
  );
  const getAcoText = (acoProviders: AcoProvider[] | undefined): string => {
    let acoText = '';
    acoProviders?.forEach((aco) => {
      if (aco?.acoNetworkDescription != null) {
        const capitalizedAco = capitalizeFirstLetterOfEachWord(
          aco.acoNetworkDescription
        );
        if (acoText !== '') {
          acoText = `${acoText}, ${capitalizedAco}`;
        } else {
          acoText = capitalizedAco;
        }
      }
    });

    if (acoText === '') {
      acoText = t('PROVIDER_LOCATIONS_DETAILS.NOT_APPLICABLE');
    }
    return acoText;
  };
  const { quality, benefits, convenience, personalFit } =
    recommendationDetails || {};
  const bandedCost = (recommendationDetails as any)?.bandedCost;
  const billingCode = (recommendationDetails as any)?.billingCode;
  const lagoonData = useLagoon('ui-messaging')();

  const quality_breakdown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_TITLE,
  });
  const convenience_breakdown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_TITLE,
  });
  const benefits_breakdown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_TITLE,
  });
  const personal_fit_breakdown = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_PERSONAL_FIT_BREAKDOWN
      .PERSONAL_FIT_BREAKDOWN_TITLE,
  });
  const getDrawerTitle = (drawerTypeString: string) => {
    switch (drawerTypeString) {
      case 'quality':
        return quality_breakdown?.message;
      case 'benefits':
        return benefits_breakdown?.message;
      case 'convenience':
        return convenience_breakdown?.message;
      case 'personalFit':
        return personal_fit_breakdown?.message;
      default:
        return '';
    }
  };

  const renderBreakdown = () => (
    <>
      {drawerType === 'quality' && (
        <QualityBreakDown
          billingCode={billingCode}
          chipBackgroundColor={getChipbackgroundColor(
            quality?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
          )}
          providerType={providerDetails?.providerType}
          quality={quality}
        />
      )}
      {drawerType === 'benefits' && (
        <BenifitsBreakDown
          acoName={getAcoText(selectedLocation?.acoProvider)}
          bandedCost={bandedCost}
          benefits={benefits}
          chipBackgroundColor={getChipbackgroundColor(
            benefits?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
          )}
          handleTabChange={handleTabChange}
          isCareAvl={isCareAvl}
          isTieredPlan={isTieredPlan}
          providerDetails={providerDetails}
          providerLocationData={selectedLocation}
          selectedLocatoionCity={selectedLocatoionCity || ''}
        />
      )}
      {drawerType === 'convenience' && (
        <ConvenienceBreakDown
          chipBackgroundColor={getChipbackgroundColor(
            convenience?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
          )}
          convenience={convenience}
          preferencesData={preferencesData}
          providerDetails={providerDetails}
          providerLocationData={selectedLocation}
        />
      )}
      {drawerType === 'personalFit' && (
        <PersonalFitBreakDown
          chipBackgroundColor={getChipbackgroundColor(
            personalFit?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
          )}
          locationKey={locationKey}
          personalFit={personalFit}
          preferencesData={preferencesData}
          providerDetails={providerDetails}
        />
      )}
    </>
  );

  if (isWidget) {
    return (
      <>
        <SmartChoiceBreakDownWidgetHeader
          handleBackButton={() => {
            adobeLinkTrackEvent({
              name: 'Back',
              location: `Smart choice ${drawerType} breakdown`,
            });
            setIsOpen();
          }}
          handleCrossButton={() => {
            adobeLinkTrackEvent({
              name: 'close',
              location: `Smart choice ${drawerType} breakdown`,
            });
            setShowSmartChoiceLevel1Breakdown(false);
            setShowSmartChoiceLevel3Breakdown(false);
          }}
          headingTitle={getDrawerTitle(drawerType)}
        />
        {renderBreakdown()}
      </>
    );
  }

  return (
    <React.Fragment>
      <StyledDrawer
        data-testid={`smart-choice-breakdown-drawer-${drawerType}`}
        isOpen={isOpen}
        onClose={() => {
          adobeLinkTrackEvent({
            name: 'close',
            location: `Smart choice ${drawerType} breakdown`,
          });
          setIsOpen();
        }}
        size={572}
        title={
          <React.Fragment>
            {haveBackButton && (
              <BackButtonContainer
                alignItems="center"
                data-testid="back-button-smart-choice-breakdown"
                direction="row"
                onClick={() => {
                  adobeLinkTrackEvent({
                    name: 'Back',
                    location: `Smart choice ${drawerType} breakdown`,
                  });
                  setIsOpen(),
                    setSmarChoiceBreakDownL1?.(true),
                    setFromL2DrawerToHaveBackButton?.();
                }}
              >
                <IconMaterial
                  color="$interactive1"
                  css={{ width: '24px', height: '24px', marginRight: '4px' }}
                  icon="arrow_back"
                  title="Back"
                />
                <Text color="$interactive1" fontWeight="$bold">
                  {t('BACK')}
                </Text>
              </BackButtonContainer>
            )}
            {getDrawerTitle(drawerType)}
          </React.Fragment>
        }
      >
        {renderBreakdown()}
      </StyledDrawer>
    </React.Fragment>
  );
};
